import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdOutlineChevronRight,
  MdSearch,
  MdShoppingCart,
  MdPayment,
  MdCalendarToday,
  MdHistory
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import BookingDetailPage from './BookingDetailPage';
import { getCustomerBookings, getBookings } from '../services/bookingService';
import { createPaymentOrder, verifyPayment } from '../services/paymentService';
import { safeParseDate } from '../utils/browserUtils';
import './BookingsPage.css';

const BookingsPage = ({ isActive, showToast, onBack, cartItemCount = 0, currentUser }) => {
  const navigate = useNavigate();
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'history'
  const [isLoading, setIsLoading] = useState(false);
  const [bookingsHistory, setBookingsHistory] = useState([]);
  const [activeBookingsList, setActiveBookingsList] = useState([]);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [activeHistoryFilter, setActiveHistoryFilter] = useState('ALL'); // 'ALL', 'COMPLETED', 'CANCELLED', 'EXPIRED', 'PAID', 'UNPAID'
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const fetchedRef = useRef(false);

  const handleBack = () => {
    if (selectedBooking) {
      setSelectedBooking(null);
    } else if (onBack) {
      onBack();
    } else {
      navigate('/account');
    }
  };

  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
  };


  const fetchMyBookings = useCallback(async () => {
    try {
      setIsLoading(true);

      // Fetch both current bookings and history in parallel
      const [currentRes, historyRes] = await Promise.all([
        getCustomerBookings(),
        getBookings()
      ]);

      const active = [];
      const history = [];

      // Unified status check logic
      const processBookings = (res, targetArray, isHistory) => {
        if (Array.isArray(res?.result)) {
          res.result.forEach(booking => {
            const statusUpper = (booking.status || '').toUpperCase();
            const isHistoryStatus = ['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(statusUpper);

            if (isHistory && isHistoryStatus) {
              targetArray.push(booking);
            } else if (!isHistory && !isHistoryStatus) {
              active.push(booking);
            }
          });
        }
      };

      processBookings(currentRes, active, false);
      processBookings(historyRes, history, true);

      // Filter out duplicates if any overlap
      const seenIds = new Set();
      const filteredActive = active.filter(b => {
        if (seenIds.has(b._id)) return false;
        seenIds.add(b._id);
        return true;
      });

      const getBookingTimestamp = (booking) => {
        const dt = booking?.updatedAt || booking?.scheduledAt || booking?.createdAt;
        const ts = dt ? safeParseDate(dt).getTime() : 0;
        return Number.isNaN(ts) ? 0 : ts;
      };

      filteredActive.sort((a, b) => getBookingTimestamp(b) - getBookingTimestamp(a));
      history.sort((a, b) => getBookingTimestamp(b) - getBookingTimestamp(a));

      setActiveBookingsList(filteredActive);
      setBookingsHistory(history);

    } catch (err) {
      console.error(err);
      showToast('Error fetching bookings');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (isActive && !fetchedRef.current) {
      fetchMyBookings();
      fetchedRef.current = true;
    }
    // Deep reset if inactive for a while? No, keep it for now.
    if (!isActive) {
      fetchedRef.current = false; // Allow refetch on reactivation
    }
  }, [isActive, fetchMyBookings]);

  // Derived filtered history
  const filteredHistory = bookingsHistory.filter(booking => {
    const status = (booking.status || 'PENDING').toUpperCase();
    const paymentStatus = (booking?.paymentStatus || '').toUpperCase();

    // Consolidated Filter Logic
    if (activeHistoryFilter !== 'ALL') {
      if (['PAID', 'UNPAID'].includes(activeHistoryFilter)) {
        if (paymentStatus !== activeHistoryFilter) return false;
      } else {
        if (status !== activeHistoryFilter) return false;
      }
    }

    // 3. Search Query
    if (historySearchQuery.trim()) {
      const query = historySearchQuery.toLowerCase();
      const serviceName = (booking?.serviceId?.serviceName || booking?.cartId?.items?.[0]?.item?.name || '').toLowerCase();
      const bookingId = (booking._id || '').toLowerCase();
      return serviceName.includes(query) || bookingId.includes(query);
    }

    return true;
  });

  const handleAction = useCallback((type, data) => {
    if (type === 'Share') {
      const shareUrl = window.location.origin;
      const shareText = `Check out my booking for ${data?.serviceId?.serviceName || 'RightTouch Service'}`;
      
      if (navigator.share) {
        navigator.share({
          title: 'RightTouch Booking',
          text: shareText,
          url: shareUrl
        }).catch(err => console.log('Error sharing:', err));
      } else {
        navigator.clipboard.writeText(`${shareText} - ${shareUrl}`);
        showToast('Booking link copied to clipboard');
      }
    } else if (type === 'Rebook') {
      const serviceName = data?.serviceId?.serviceName || data?.serviceName;
      if (serviceName) {
        navigate(`/services?search=${encodeURIComponent(serviceName)}`);
      } else {
        navigate('/services');
      }
    }
  }, [navigate, showToast]);
  
  const handlePayNow = async (booking) => {
    if (paymentLoading) return;
    setPaymentLoading(true);
    try {
      const bookingId = booking._id;
      
      // 1. Create Razorpay Order
      const orderRes = await createPaymentOrder({ bookingId });
      console.log('[Razorpay] Order Creation Response:', orderRes);

      if (!orderRes?.success || !orderRes.result) {
        throw new Error(orderRes?.message || 'Failed to create payment order from server');
      }

      // Destructure with fallbacks
      const { 
        amount: rawAmount, 
        orderId, 
        keyId, 
        key, 
        currency = 'INR' 
      } = orderRes.result;

      // ✅ Always use backend key first
      const finalKey = (keyId || key || process.env.REACT_APP_RAZORPAY_KEY_ID || "").trim();
      const finalOrderId = (orderId || "").trim();
      const finalCurrency = String(currency).toUpperCase();

      console.log("Frontend Key (Bookings):", finalKey);
      console.log("Order ID (Bookings):", finalOrderId);

      if (!finalKey) throw new Error('Razorpay Key ID is missing');
      if (!finalOrderId) throw new Error('Razorpay Order ID is missing');
      if (!rawAmount) throw new Error('Payment amount is missing');

      // 2. Amount unit handling (ensure it is in paise)
      // Heuristic: If amount is > 1000 and total matches, it might already be in paise
      // But the safest way is to trust the server and multiply if it looks like rupees (small value)
      // However, to be robust, we check if the server amount matches the booking's total
      let amountInPaise = Math.round(Number(rawAmount) * 100);
      
      // If the raw amount is already very large (e.g. > 10000 for a 100 rupee item), 
      // it's likely already in paise.
      if (Number(rawAmount) > (booking.totalPrice || 10000)) {
        console.warn('[Razorpay] Amount from server seems to be already in paise:', rawAmount);
        amountInPaise = Math.round(Number(rawAmount));
      }

      if (amountInPaise < 100) {
        throw new Error('Minimum payment amount is ₹1.00 (100 paise)');
      }

      // Clean phone number
      const cleanPhone = (currentUser?.phone || currentUser?.mobile || "").replace(/\D/g, "");

      // 3. Open Razorpay Checkout
      const options = {
        key: finalKey,
        amount: amountInPaise,
        currency: finalCurrency,
        name: "RightTouch",
        description: `Payment for Booking #${bookingId?.slice(-6).toUpperCase()}`,
        order_id: finalOrderId,
        prefill: {
          name: (currentUser?.name || currentUser?.fname || "Customer").trim(),
          email: (currentUser?.email || "").trim(),
          contact: cleanPhone.length >= 10 ? cleanPhone : ""
        },
        theme: {
          color: "#22c55e"
        },
        handler: async function (response) {
          try {
            console.log('[Razorpay Success] Response:', response);
            // 4. Verify Payment
            const verifyRes = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              bookingId: bookingId
            });

            if (verifyRes?.success) {
              showToast('Payment Successful!', 'success');
              await fetchMyBookings();
              setSelectedBooking(null);
            } else {
              showToast(verifyRes?.message || 'Payment verification failed', 'error');
            }
          } catch (error) {
            console.error('[Razorpay Verify Error]:', error);
            showToast('Error verifying payment', 'error');
          } finally {
            setPaymentLoading(false);
          }
        },
        modal: {
          ondismiss: function() {
            console.log('[Razorpay] Checkout dismissed by user');
            setPaymentLoading(false);
          }
        }
      };

      console.log('[Razorpay Options] Final Payload:', { ...options, key: finalKey.substring(0, 8) + '***' });
      
      try {
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response){
                console.error('[Razorpay Payment Failed]:', response.error);
                showToast(response.error.description || 'Payment failed', 'error');
        });
        rzp.open();
      } catch (e) {
        console.error('[Razorpay Init Error]:', e);
        showToast('Failed to open payment gateway. Check console.', 'error');
        setPaymentLoading(false);
      }

    } catch (error) {
      console.error('[Payment Flow Error]:', error);
      showToast(error.message || 'Payment initialization failed', 'error');
      setPaymentLoading(false);
    }

  };

  // If a booking is selected, show the detail page
  if (selectedBooking) {
    const status = (selectedBooking.status || '').toUpperCase();
    const isPaid = (selectedBooking.paymentStatus || '').toUpperCase() === 'PAID';
    const canPay = status === 'COMPLETED' && !isPaid;

    return (
      <BookingDetailPage
        booking={selectedBooking}
        onBack={handleBack}
        handleAction={handleAction}
        showToast={showToast}
        currentUser={currentUser}
        isPaidBooking={isPaid}
        canShowPayNow={canPay}
        paymentLoading={paymentLoading}
        handlePayButtonClick={() => {
          if (isPaid) {
            setShowInvoice(true);
          } else {
            handlePayNow(selectedBooking);
          }
        }}
        canRate={status === 'COMPLETED'}
        ratingForm={{ rates: 5, comment: '' }}
        setRatingForm={() => {}} // Pass stubs if logic is complex
        handleSubmitRating={() => showToast('Rating submitted')}
        showInvoice={showInvoice}
        setShowInvoice={setShowInvoice}
      />
    );
  }

  const renderBookingCard = (booking) => {
    const serviceName = booking?.serviceId?.serviceName || booking?.cartId?.items?.[0]?.item?.name || 'Service Booking';
    const status = (booking.status || 'PENDING').toUpperCase();
    const paymentStatus = (booking?.paymentStatus || '').toUpperCase();
    const isPaymentPending = status === 'COMPLETED' && paymentStatus !== 'PAID';
    const paymentLabel = paymentStatus === 'PAID' ? 'PAID' : 'UNPAID';
    const date = booking.scheduledAt ? safeParseDate(booking.scheduledAt).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }) : (booking.createdAt ? safeParseDate(booking.createdAt).toLocaleDateString() : 'No date');

    const getStatusClass = (status) => {
      if (['COMPLETED', 'ASSIGNED', 'ACCEPTED', 'IN PROGRESS'].includes(status)) return 'status-success';
      if (['CANCELLED', 'EXPIRED'].includes(status)) return 'status-error';
      return 'status-warning';
    };

    return (
      <div
        key={booking._id}
        className="booking-card-premium"
        onClick={() => handleBookingClick(booking)}
      >
        <div className="booking-card-header">
          <div className="booking-icon-wrapper">
            <MdShoppingCart className="booking-card-icon" />
          </div>
          <div className="booking-title-group">
            <h4 className="booking-service-name">{serviceName}</h4>
            <p className="booking-id-text">ID: #{booking._id?.slice(-6).toUpperCase()}</p>
          </div>
          <div className="booking-badges">
            <div className={`status-badge-vibrant ${getStatusClass(status)}`}>
              {status}
            </div>
            {status !== 'EXPIRED' && status !== 'CANCELLED' && (
              <div className={`payment-status-badge ${paymentStatus === 'PAID' ? 'paid' : 'unpaid'}`}>
                {paymentLabel}
              </div>
            )}
          </div>
        </div>

        {isPaymentPending && (
          <div className="payment-pending-banner">
            <MdPayment style={{ marginRight: '6px', flexShrink: 0 }} />
            Payment Pending - Tap to pay
          </div>
        )}

        <div className="booking-card-body">
          <div className="booking-info-row">
            <span className="info-label">Scheduled Date</span>
            <span className="info-value">{date}</span>
          </div>
          <div className="booking-info-row">
            <span className="info-label">Total Amount</span>
            <span className="info-value-price">₹{booking.totalPrice || booking.baseAmount || 0}</span>
          </div>
          
          {booking.technicianId && (
            <div className="booking-tech-preview">
              <div className="tech-avatar-mini">
                {booking.technicianId.profileImage ? (
                  <img src={booking.technicianId.profileImage} alt="Tech" />
                ) : (
                  <div className="avatar-placeholder">{booking.technicianId.userId?.fname?.charAt(0) || 'T'}</div>
                )}
              </div>
              <div className="tech-info-mini">
                <span className="tech-role">Assigned Expert</span>
                <span className="tech-name">{booking.technicianId.userId?.fname} {booking.technicianId.userId?.lname}</span>
              </div>
            </div>
          )}
        </div>

        <div className="booking-card-footer">
          <span className="view-details-text">View Details</span>
          <MdOutlineChevronRight className="arrow-icon-premium" />
        </div>
      </div>
    );
  };

  return (
    <section className={`bookings-page-premium ${isActive ? '' : 'hidden'}`}>
      {/* Header Section - Desktop Only */}
      <div className="bookings-premium-header desktop-only">
        <div className="header-top">
          <button className="back-btn-premium" onClick={handleBack}>
            <MdOutlineChevronRight className="back-icon-premium" />
          </button>
          <h2 className="page-title-premium">My Bookings</h2>
        </div>
      </div>

      {/* Tab Navigation - Visible on all screens */}
      <div className="bookings-tabs-premium-fixed">
        <div className="bookings-tabs-premium">
          <button
            className={`tab-btn-premium ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            <MdCalendarToday style={{ marginBottom: '-2px', marginRight: '6px' }} />
            Active & Upcoming
          </button>
          <button
            className={`tab-btn-premium ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <MdHistory style={{ marginBottom: '-2px', marginRight: '6px' }} />
            Past Bookings
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className="bookings-container-premium">
        {isLoading ? (
          <div className="loading-state-premium">
            <div className="shimmer-card"></div>
            <div className="shimmer-card"></div>
          </div>
        ) : (
          <div className="bookings-list-premium">
            {activeTab === 'active' ? (
              activeBookingsList.length > 0 ? (
                activeBookingsList.map(booking => renderBookingCard(booking))
              ) : (
                <div className="empty-state-premium">
                  <div className="empty-icon-wrapper">📅</div>
                  <h3>No active bookings</h3>
                  <p>You haven't booked any services yet.</p>
                  <button className="book-now-btn" onClick={() => navigate('/')}>Book Now</button>
                </div>
              )
            ) : (
              <>
                <div className="history-filters-premium">
                  <div className="history-search-wrapper">
                    <MdSearch className="search-icon-dim" />
                    <input
                      type="text"
                      placeholder="Search by service or ID..."
                      value={historySearchQuery}
                      onChange={(e) => setHistorySearchQuery(e.target.value)}
                      className="history-search-input"
                    />
                  </div>
                  <div className="status-filter-pills-container">
                    <div className="status-filter-pills">
                      {['ALL', 'COMPLETED', 'CANCELLED', 'EXPIRED', 'PAID', 'UNPAID'].map(filter => (
                        <button
                          key={filter}
                          className={`filter-pill ${activeHistoryFilter === filter ? 'active' : ''}`}
                          onClick={() => setActiveHistoryFilter(filter)}
                        >
                          {filter}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {filteredHistory.length > 0 ? (
                  filteredHistory.map(booking => renderBookingCard(booking))
                ) : (
                  <div className="empty-state-premium">
                    <div className="empty-icon-wrapper">⌛</div>
                    <h3>No bookings found</h3>
                    <p>Try adjusting your search or filters.</p>
                    {(historySearchQuery || activeHistoryFilter !== 'ALL') && (
                      <button 
                        className="view-details-link" 
                        style={{ marginTop: '12px', background: 'none', border: 'none', color: 'var(--green)', fontWeight: '700', cursor: 'pointer' }}
                        onClick={() => { setHistorySearchQuery(''); setActiveHistoryFilter('ALL'); }}
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default BookingsPage;